import React, { useEffect } from 'react';
import { Alert, Box, Button, Collapse, Stack } from '@mui/material';
import type { SnackbarItem } from 'atoms/snackbarAtom';

// 自動消去までの時間（従来の単発 Snackbar の autoHideDuration を踏襲）。
const AUTO_HIDE_MS = 4000;

interface ItemProps {
    item: SnackbarItem;
    onClose: (id: number) => void;
}

// 1 件の通知。マウント時に自動消去タイマーを張り、積み重ね表示の各要素が独立に消える。
const SnackbarStackItem: React.FC<ItemProps> = ({ item, onClose }) => {
    useEffect(() => {
        const timer = window.setTimeout(() => onClose(item.id), AUTO_HIDE_MS);
        return () => window.clearTimeout(timer);
    }, [item.id, onClose]);

    return (
        <Collapse in>
            <Alert
                severity={item.severity}
                onClose={() => onClose(item.id)}
                action={
                    item.action ? (
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => {
                                item.action?.onClick();
                                onClose(item.id);
                            }}
                        >
                            {item.action.label}
                        </Button>
                    ) : undefined
                }
                sx={{ width: '100%', boxShadow: 3 }}
            >
                {item.message}
            </Alert>
        </Collapse>
    );
};

interface Props {
    items: SnackbarItem[];
    onClose: (id: number) => void;
}

// 通知キューを画面下中央に積み重ねて表示する（重ならず縦に並ぶ）。
// データは props 経由で受け取り、atom は直接参照しない（直接参照は MainPage に閉じる）。
export const SnackbarStack: React.FC<Props> = ({ items, onClose }) => {
    if (items.length === 0) return null;
    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: (theme) => theme.zIndex.snackbar,
                width: 'min(92vw, 420px)',
                // 背後の操作を塞がない。個々の Alert だけクリック可能にする。
                pointerEvents: 'none',
            }}
        >
            <Stack spacing={1} sx={{ '& .MuiAlert-root': { pointerEvents: 'auto' } }}>
                {items.map((item) => (
                    <SnackbarStackItem key={item.id} item={item} onClose={onClose} />
                ))}
            </Stack>
        </Box>
    );
};
